import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Instagram, Link2, Unlink, Loader2, CheckCircle2, Zap, ShieldCheck, XCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";

function Toggle({ icon: Icon, title, hint, checked, disabled, onChange, testid }) {
  return (
    <label className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${disabled ? "border-dashed border-[#ccd0d5] opacity-60" : "border-[#ccd0d5]"}`}>
      <Icon className="w-5 h-5 text-[#1877f2]" />
      <span className="flex-1">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs text-[#65676b]">{hint}</span>
      </span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function Perm({ ok, label, testid }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${ok ? "bg-[#e9f7ef] text-green-700" : "bg-red-50 text-red-600"}`} data-testid={testid}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {label} : {ok ? "autorisée" : "non accordée"}
    </span>
  );
}

function Perms({ conn }) {
  return (
    <div className="space-y-2" data-testid="meta-permissions">
      <div className="flex flex-wrap gap-2">
        <Perm ok={conn.can_publish_fb} label="Publication Facebook" testid="meta-perm-fb" />
        <Perm ok={conn.can_publish_ig} label="Publication Instagram" testid="meta-perm-ig" />
      </div>
      {!conn.can_publish_fb && (
        <p className="text-xs text-[#65676b]" data-testid="meta-perm-hint">Sans cette autorisation, BuzzlyMe passe par Upload-Post (limite 25/24 h). Cliquez sur « Reconnecter » et acceptez la publication sur votre page.</p>
      )}
    </div>
  );
}

function AddPage({ onAdd, busy }) {
  const [ref, setRef] = useState("");
  return (
    <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (ref.trim()) onAdd(ref.trim()); }}>
      <input value={ref} onChange={e => setRef(e.target.value)} placeholder="Lien ou ID de votre Page Facebook" data-testid="meta-add-page-input"
        className="flex-1 min-w-0 rounded-lg border border-[#ccd0d5] px-3 py-2 text-sm outline-none focus:border-[#1877f2]" />
      <button type="submit" className="btn-primary" disabled={busy || !ref.trim()} data-testid="meta-add-page-btn">Ajouter</button>
    </form>
  );
}

function Pages({ conn, onPick, onAdd, busy }) {
  if (!conn.pages.length) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-[#65676b] rounded-lg bg-[#fff7e6] px-4 py-3" data-testid="meta-no-pages">Facebook n'a renvoyé aucune page. Collez le lien de votre Page (ex. https://www.facebook.com/votrepage) ou son ID :</p>
        <AddPage onAdd={onAdd} busy={busy} />
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="meta-page-picker">
      <p className="text-sm font-semibold">{conn.page ? "Page utilisée :" : "Choisissez la page sur laquelle publier :"}</p>
      {conn.pages.map(p => (
        <button key={p.id} type="button" disabled={busy} onClick={() => onPick(p.id)} data-testid={`meta-page-option-${p.id}`}
          className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:border-[#1877f2] ${conn.page?.id === p.id ? "border-[#1877f2] bg-[#e7f3ff]" : "border-[#ccd0d5]"}`}>
          {p.picture ? <img src={p.picture} alt="" className="w-8 h-8 rounded-full" /> : <Facebook className="w-8 h-8 text-[#1877f2]" />}
          <span className="flex-1 font-semibold text-sm">{p.name}</span>
          {p.ig_username && <span className="text-xs text-[#65676b] flex items-center gap-1"><Instagram className="w-3.5 h-3.5" />@{p.ig_username}</span>}
          {conn.page?.id === p.id && <CheckCircle2 className="w-4 h-4 text-green-600" />}
        </button>
      ))}
    </div>
  );
}

export const MetaConnect = () => {
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.metaConnection().then(setConn).catch(() => setConn({ available: false })), []);
  useEffect(() => {
    load();
    const st = params.get("meta");
    if (st) {
      st === "connected" ? toast.success("Compte Meta connecté") : toast.error(params.get("reason") || "Connexion Meta échouée");
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.url) { window.location.href = res.url; return; }
      setConn(res);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!conn || !conn.available) return null;
  const ig = conn.page?.ig_id;
  const set = (body, ok) => run(() => api.metaSettings(body), ok);
  return (
    <section className="card-surface p-6 space-y-4" data-testid="meta-connect-card">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1877f2] to-[#e1306c] grid place-items-center"><ShieldCheck className="w-5 h-5 text-white" /></span>
        <div>
          <h2 className="font-display text-lg font-bold">Option : connexion Meta directe</h2>
          <p className="text-sm text-[#65676b]">Facebook + Instagram via l'app officielle BuzzlyMe. Si elle est active, elle est utilisée à la place de la connexion ci-dessus.</p>
        </div>
      </div>

      {conn.connected ? (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-[#e9f7ef] px-4 py-3" data-testid="meta-connected">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm">Compte <b data-testid="meta-account-name">{conn.account_name}</b></span>
            <button type="button" className="btn-ghost ml-auto !py-1.5" disabled={busy} data-testid="meta-reconnect-btn"
              onClick={() => run(() => api.metaConnect(window.location.origin))}>
              <Link2 className="w-4 h-4" /> Reconnecter
            </button>
            <button type="button" className="btn-ghost !py-1.5" disabled={busy} data-testid="meta-disconnect-btn"
              onClick={() => window.confirm("Déconnecter la connexion Meta directe ?") && run(() => api.metaDisconnect(), "Connexion Meta supprimée")}>
              <Unlink className="w-4 h-4" /> Déconnecter
            </button>
          </div>
          <Perms conn={conn} />
          <Pages conn={conn} busy={busy} onPick={(id) => run(() => api.metaChoosePage(id), "Page sélectionnée")}
            onAdd={(ref) => run(() => api.metaAddPage(ref), "Page ajoutée")} />
          <Toggle icon={Facebook} title="Publier sur Facebook" hint={conn.page ? `Sur la page ${conn.page.name}` : "Choisissez d'abord une page"}
            checked={conn.fb_enabled} disabled={!conn.page || busy} onChange={(v) => set({ fb_enabled: v })} testid="meta-fb-switch" />
          <Toggle icon={Instagram} title="Publier sur Instagram (Reels)" hint={ig ? `Sur @${conn.page.ig_username}` : "Aucun compte Instagram pro lié à cette page"}
            checked={conn.ig_enabled} disabled={!ig || busy} onChange={(v) => set({ ig_enabled: v })} testid="meta-ig-switch" />
          <Toggle icon={Zap} title="Publication automatique" hint="Chaque vidéo terminée part tout de suite sur les réseaux activés."
            checked={conn.auto_publish} disabled={!conn.page || busy} onChange={(v) => set({ auto_publish: v })} testid="meta-auto-switch" />
        </>
      ) : (
        <button type="button" className="btn-primary" disabled={busy} data-testid="meta-connect-btn"
          onClick={() => run(() => api.metaConnect(window.location.origin))}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connecter via Meta (direct)
        </button>
      )}
    </section>
  );
};
